import React, { useState, useEffect } from 'react';
import { Archive, Eye, RotateCcw, DownloadCloud } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, PageHeader, Badge, EmptyState, Spinner } from '../components/ui';
import { backupsApi, archivesApi } from '../lib/api';
import type { Snapshot } from '../types/vault';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function Snapshots() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<string | null>(null);

  useEffect(() => {
    loadSnapshots();
  }, []);

  const loadSnapshots = async () => {
    setLoading(true);
    try {
      const res = await backupsApi.listSnapshots();
      if (res.success && res.data) setSnapshots(res.data.snapshots);
    } catch {
      setError('Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id: string) => {
    setArchiving(id);
    try {
      const res = await archivesApi.triggerArchive(id);
      if (res.success) {
        loadSnapshots();
      } else {
        setError(res.message || 'Failed to trigger archive');
      }
    } catch {
      setError('Failed to trigger archive');
    } finally {
      setArchiving(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Storage"
          title="Snapshots"
          description="Browse completed backup snapshots"
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            {error}
            <button onClick={() => setError(null)} className="ml-2 font-bold">&times;</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : snapshots.length === 0 ? (
          <EmptyState
            icon={Archive}
            title="No snapshots yet"
            description="Complete a backup to create your first snapshot."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {snapshots.map((snap) => (
              <Card key={snap.id} padded>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display font-semibold text-ink">{snap.connectionName || snap.platform}</h3>
                    <p className="text-sm text-ink-3 capitalize">{snap.platform}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-ink font-mono">{snap.totalItems} items</span>
                    <p className="text-xs text-ink-3 font-mono">{formatBytes(snap.sizeBytes)}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {snap.resourceTypes.map((type) => (
                    <span key={type} className="px-2 py-0.5 text-xs bg-surface-2 text-ink-2 rounded font-mono">
                      {type}
                    </span>
                  ))}
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-ink-3">
                  <span className="font-mono">Created: {new Date(snap.createdAt).toLocaleString()}</span>
                  {snap.b2Archived ? (
                    <Badge tone="success">Archived</Badge>
                  ) : (
                    <Badge tone="warning">Not archived</Badge>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-line-2">
                  <Link
                    to={`/vault/snapshots/${snap.id}/browse`}
                    className="flex items-center px-3 py-1.5 text-sm bg-accent-soft text-accent-ink rounded-[10px] hover:opacity-80 transition"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Browse
                  </Link>
                  {!snap.b2Archived && (
                    <button
                      onClick={() => handleArchive(snap.id)}
                      disabled={archiving === snap.id}
                      className="flex items-center px-3 py-1.5 text-sm bg-surface-2 text-ink-2 rounded-[10px] hover:bg-bg disabled:opacity-50 transition"
                    >
                      <DownloadCloud className="w-3 h-3 mr-1" />
                      {archiving === snap.id ? 'Archiving...' : 'Archive to B2'}
                    </button>
                  )}
                  <Link
                    to={`/vault/restores?snapshotId=${snap.id}`}
                    className="flex items-center px-3 py-1.5 text-sm bg-surface-2 text-ink-2 rounded-[10px] hover:bg-bg transition"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Restore
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
